export type ApiErrorShape = { error?: { message?: string; code?: string } };

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
  ) {
    super(message);
  }
}

export async function api<T>(path: string, options: RequestInit = {}, organizationId?: string | null): Promise<T> {
  const headers = new Headers(options.headers);
  if (options.body && !headers.has("content-type")) headers.set("content-type", "application/json");
  if (organizationId) headers.set("x-organization-id", organizationId);
  const response = await fetch(path, { ...options, headers, credentials: "same-origin" });
  const data = (await response.json().catch(() => ({}))) as T & ApiErrorShape;
  if (!response.ok) throw new ApiError(data.error?.message ?? `Request failed (${response.status})`, response.status, data.error?.code);
  return data;
}

export function formatNumber(value: number, maximumFractionDigits = 1): string {
  return new Intl.NumberFormat("en-US", { notation: value >= 10_000 ? "compact" : "standard", maximumFractionDigits }).format(value);
}

export function formatCount(count: number, singular: string, plural = `${singular}s`): string {
  return `${new Intl.NumberFormat("en-US").format(count)} ${count === 1 ? singular : plural}`;
}

export function formatDuration(value: number | null | undefined): string {
  if (value == null) return "—";
  if (value < 1_000) return `${Math.round(value)}ms`;
  if (value < 60_000) return `${(value / 1_000).toFixed(value < 10_000 ? 1 : 0)}s`;
  if (value < 3_600_000) return `${Math.round(value / 60_000)}m`;
  return `${(value / 3_600_000).toFixed(1)}h`;
}

export function formatRelative(value: string | null | undefined): string {
  if (!value) return "Never";
  const seconds = Math.round((new Date(value).getTime() - Date.now()) / 1_000);
  const formatter = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  const ranges: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ["year", 31_536_000],
    ["month", 2_592_000],
    ["week", 604_800],
    ["day", 86_400],
    ["hour", 3_600],
    ["minute", 60],
  ];
  for (const [unit, divisor] of ranges) {
    if (Math.abs(seconds) >= divisor) return formatter.format(Math.round(seconds / divisor), unit);
  }
  return formatter.format(seconds, "second");
}

export function statusTone(status: string | null | undefined): "good" | "bad" | "live" | "quiet" {
  if (!status) return "quiet";
  if (["succeeded", "success", "completed", "active"].includes(status)) return "good";
  if (["failed", "error"].includes(status)) return "bad";
  if (["running", "queued"].includes(status)) return "live";
  return "quiet";
}
